/**
 * Sobriety milestone definitions and computation.
 */

export type BadgeTier = 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond'

export interface Milestone {
  id: string
  name: string
  tier: BadgeTier
  tierLevel: number        // 1, 2, 3 within tier
  durationSeconds: number  // seconds of sobriety required
  icon: string             // emoji
  healthFact: string       // what's improving at this stage
}

export interface EarnedMilestone extends Milestone {
  earnedAt: string         // ISO timestamp when first earned
  streakStartedAt: string  // which streak earned it
}

// Tier colours
export const TIER_COLORS: Record<BadgeTier, string> = {
  bronze: '#CD7F32',
  silver: '#A8A9AD',
  gold: '#FFD700',
  platinum: '#E5E4E2',
  diamond: '#7DD3FC',
}

export const TIER_LABELS: Record<BadgeTier, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
  diamond: 'Diamond',
}

const DAY = 86400
const WEEK = DAY * 7
const MONTH = DAY * 30
const YEAR = DAY * 365

export const MILESTONES: Milestone[] = [
  // Bronze tier
  {
    id: 'bronze-1',
    name: 'First Day',
    tier: 'bronze',
    tierLevel: 1,
    durationSeconds: DAY,
    icon: '🌱',
    healthFact: 'Your blood alcohol has returned to zero and your body has begun its recovery process.',
  },
  {
    id: 'bronze-2',
    name: 'Two Days Strong',
    tier: 'bronze',
    tierLevel: 2,
    durationSeconds: DAY * 2,
    icon: '💪',
    healthFact: 'Your body is rehydrating and blood sugar levels are stabilising. Sleep quality is starting to improve.',
  },
  {
    id: 'bronze-3',
    name: 'Three Day Milestone',
    tier: 'bronze',
    tierLevel: 3,
    durationSeconds: DAY * 3,
    icon: '⭐',
    healthFact: 'Dopamine levels are beginning to normalise. Your brain is recalibrating its reward system.',
  },
  {
    id: 'bronze-4',
    name: 'Four Days',
    tier: 'bronze',
    tierLevel: 4,
    durationSeconds: DAY * 4,
    icon: '✨',
    healthFact: 'Sleep is deeper and more restorative. Anxiety is fading as your nervous system finds its rhythm.',
  },

  // Silver tier
  {
    id: 'silver-1',
    name: 'Five Days Clear',
    tier: 'silver',
    tierLevel: 1,
    durationSeconds: DAY * 5,
    icon: '🛡️',
    healthFact: 'Most acute withdrawal symptoms have passed. Your liver is actively repairing itself.',
  },
  {
    id: 'silver-2',
    name: 'One Week',
    tier: 'silver',
    tierLevel: 2,
    durationSeconds: WEEK,
    icon: '🎯',
    healthFact: 'Sleep quality is noticeably improving. Your skin is better hydrated and starting to look healthier.',
  },
  {
    id: 'silver-3',
    name: 'Two Weeks',
    tier: 'silver',
    tierLevel: 3,
    durationSeconds: WEEK * 2,
    icon: '🏔️',
    healthFact: 'Stomach lining is healing and acid reflux is reducing. Your immune system is strengthening.',
  },
  {
    id: 'silver-4',
    name: 'Three Weeks',
    tier: 'silver',
    tierLevel: 4,
    durationSeconds: WEEK * 3,
    icon: '🧗',
    healthFact: 'Liver enzymes continue to drop. Inflammation throughout your body is markedly reduced.',
  },

  // Gold tier
  {
    id: 'gold-1',
    name: 'One Month',
    tier: 'gold',
    tierLevel: 1,
    durationSeconds: MONTH,
    icon: '🏆',
    healthFact: 'Liver fat can reduce by up to 15%. Blood pressure is lowering. Your skin looks visibly healthier.',
  },
  {
    id: 'gold-2',
    name: 'Two Months',
    tier: 'gold',
    tierLevel: 2,
    durationSeconds: MONTH * 2,
    icon: '🔥',
    healthFact: 'Liver function is significantly improved. Mental clarity and focus are noticeably sharper.',
  },
  {
    id: 'gold-3',
    name: 'Three Months',
    tier: 'gold',
    tierLevel: 3,
    durationSeconds: MONTH * 3,
    icon: '👑',
    healthFact: 'Brain chemistry has substantially rebalanced. Emotional regulation and cognitive function are markedly improved.',
  },
  {
    id: 'gold-4',
    name: 'Four Months',
    tier: 'gold',
    tierLevel: 4,
    durationSeconds: MONTH * 4,
    icon: '🌅',
    healthFact: 'Cravings have substantially diminished. Your relationship with alcohol has fundamentally shifted.',
  },
  {
    id: 'gold-5',
    name: 'Five Months',
    tier: 'gold',
    tierLevel: 5,
    durationSeconds: MONTH * 5,
    icon: '⛰️',
    healthFact: 'Stress resilience is measurably better. Your body is settling into its sober equilibrium.',
  },

  // Platinum tier
  {
    id: 'platinum-1',
    name: 'Six Months',
    tier: 'platinum',
    tierLevel: 1,
    durationSeconds: MONTH * 6,
    icon: '🌟',
    healthFact: 'Liver function may have fully normalised. Risk of several cancers is already reducing.',
  },
  {
    id: 'platinum-3',
    name: 'Seven Months',
    tier: 'platinum',
    tierLevel: 2,
    durationSeconds: MONTH * 7,
    icon: '🪐',
    healthFact: 'Cardiovascular markers continue to improve. Cholesterol balance is optimising.',
  },
  {
    id: 'platinum-4',
    name: 'Eight Months',
    tier: 'platinum',
    tierLevel: 3,
    durationSeconds: MONTH * 8,
    icon: '🌌',
    healthFact: 'Long-term inflammation has dropped substantially. Your cells are repairing at a deep level.',
  },
  {
    id: 'platinum-2',
    name: 'Nine Months',
    tier: 'platinum',
    tierLevel: 4,
    durationSeconds: MONTH * 9,
    icon: '🦅',
    healthFact: 'Energy levels, mental health, and physical fitness are dramatically improved. Your body has done remarkable healing.',
  },
  {
    id: 'platinum-5',
    name: 'Ten Months',
    tier: 'platinum',
    tierLevel: 5,
    durationSeconds: MONTH * 10,
    icon: '🏝️',
    healthFact: 'Mental clarity, mood stability, and physical wellbeing are at peak levels you may not have felt in years.',
  },
  {
    id: 'platinum-6',
    name: 'Eleven Months',
    tier: 'platinum',
    tierLevel: 6,
    durationSeconds: MONTH * 11,
    icon: '🦋',
    healthFact: "You're standing at the edge of a full year. Your transformation is profound and visible.",
  },

  // Diamond tier
  {
    id: 'diamond-1',
    name: 'One Year',
    tier: 'diamond',
    tierLevel: 1,
    durationSeconds: YEAR,
    icon: '💎',
    healthFact: 'Risk of heart disease has dropped significantly. Your overall cancer risk continues to decrease with every sober day.',
  },
  {
    id: 'diamond-4',
    name: 'Eighteen Months',
    tier: 'diamond',
    tierLevel: 2,
    durationSeconds: MONTH * 18,
    icon: '🌠',
    healthFact: 'Brain plasticity has rewired pathways once shaped by alcohol. Your default state is now sober.',
  },
  {
    id: 'diamond-2',
    name: 'Two Years',
    tier: 'diamond',
    tierLevel: 3,
    durationSeconds: YEAR * 2,
    icon: '🌈',
    healthFact: 'Brain structure and function have substantially recovered. Your risk profile now approaches that of a non-drinker for many conditions.',
  },
  {
    id: 'diamond-3',
    name: 'Three Years',
    tier: 'diamond',
    tierLevel: 4,
    durationSeconds: YEAR * 3,
    icon: '🏛️',
    healthFact: 'Liver damage has had extensive time to repair. Your overall mortality risk has significantly decreased.',
  },
  {
    id: 'diamond-5',
    name: 'Five Years',
    tier: 'diamond',
    tierLevel: 5,
    durationSeconds: YEAR * 5,
    icon: '🌳',
    healthFact: 'Most alcohol-related health risks have returned to or below population baseline. You have fundamentally rewritten your future health trajectory.',
  },
  {
    id: 'diamond-6',
    name: 'Ten Years',
    tier: 'diamond',
    tierLevel: 6,
    durationSeconds: YEAR * 10,
    icon: '🗿',
    healthFact: 'A decade of sustained sobriety. The cumulative benefits to body, mind, and life are immeasurable.',
  },
]

// Special milestone for personal best
export const PERSONAL_BEST_MILESTONE: Omit<Milestone, 'durationSeconds'> = {
  id: 'personal-best',
  name: 'Personal Best',
  tier: 'gold',
  tierLevel: 0,
  icon: '🥇',
  healthFact: 'You just beat your longest sober streak. Every new record is proof of your growing strength.',
}

/**
 * Liver-recovery model — full functional recovery treated as ~6 months sober.
 * Aligned with the 6-month "Liver function may have fully normalised" milestone.
 */
export const LIVER_RECOVERY_SECONDS = MONTH * 6

/**
 * Decimal places needed for the percent reading to advance every second.
 * Computed so the smallest displayed unit (10^-N) is ≤ the per-second increment.
 */
export const LIVER_RECOVERY_DECIMALS = Math.max(
  2,
  Math.ceil(-Math.log10(100 / LIVER_RECOVERY_SECONDS))
)

export function liverRecoveryPercent(streakSeconds: number): number {
  if (streakSeconds <= 0) return 0
  return Math.min(100, (streakSeconds / LIVER_RECOVERY_SECONDS) * 100)
}

/**
 * Compute which milestones have been earned from sober streaks.
 * Returns earned milestones sorted by duration ascending.
 */
export function computeEarnedMilestones(
  soberStreaks: { startedAt: string; endedAt: string; durationSeconds: number }[]
): EarnedMilestone[] {
  const earned: EarnedMilestone[] = []

  for (const milestone of MILESTONES) {
    // Find all qualifying streaks, then pick the most recent one (latest startedAt)
    const qualifying = soberStreaks.filter(s => s.durationSeconds >= milestone.durationSeconds)
    if (qualifying.length > 0) {
      const mostRecent = qualifying.reduce((latest, s) =>
        new Date(s.startedAt).getTime() > new Date(latest.startedAt).getTime() ? s : latest
      )
      // Earned time = streak start + milestone duration
      const earnedAt = new Date(new Date(mostRecent.startedAt).getTime() + milestone.durationSeconds * 1000).toISOString()
      earned.push({
        ...milestone,
        earnedAt,
        streakStartedAt: mostRecent.startedAt,
      })
    }
  }

  return earned
}

/**
 * Get the current live sober streak duration in seconds, or 0 if not sober.
 */
export function getLiveStreakSeconds(
  soberStreaks: { startedAt: string; endedAt: string; durationSeconds: number; isLive?: boolean }[],
  currentBAC: number
): number {
  if (currentBAC > 0 || soberStreaks.length === 0) return 0
  const liveStreak = soberStreaks.find(s => s.isLive)
  return liveStreak?.durationSeconds || 0
}

/**
 * Get the next milestone the current streak is approaching.
 */
export function getNextMilestone(currentStreakSeconds: number): Milestone | null {
  return MILESTONES.find(m => m.durationSeconds > currentStreakSeconds) || null
}

/**
 * Get the highest milestone the current live streak qualifies for.
 * Returns null when the streak hasn't reached the first milestone.
 */
export function getCurrentMilestone(currentStreakSeconds: number): Milestone | null {
  let current: Milestone | null = null
  for (const m of MILESTONES) {
    if (m.durationSeconds <= currentStreakSeconds) {
      current = m
    } else {
      break
    }
  }
  return current
}

/**
 * Format a duration in seconds as a granular ticker:
 * "1y 2mo 3w 4d 5h 6m 7s", omitting any unit that is zero AND outranked by
 * a higher non-zero unit. Always shows seconds so live counters tick.
 */
export function formatGranularDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  let rem = total
  const yrs = Math.floor(rem / YEAR); rem -= yrs * YEAR
  const mths = Math.floor(rem / MONTH); rem -= mths * MONTH
  const wks = Math.floor(rem / WEEK); rem -= wks * WEEK
  const days = Math.floor(rem / DAY); rem -= days * DAY
  const hrs = Math.floor(rem / 3600); rem -= hrs * 3600
  const mins = Math.floor(rem / 60); rem -= mins * 60
  const secs = rem

  const parts: string[] = []
  if (yrs > 0) parts.push(`${yrs}y`)
  if (mths > 0) parts.push(`${mths}mo`)
  if (wks > 0) parts.push(`${wks}w`)
  if (days > 0) parts.push(`${days}d`)
  if (hrs > 0) parts.push(`${hrs}h`)
  if (mins > 0) parts.push(`${mins}m`)
  parts.push(`${secs}s`)
  return parts.join(' ')
}

export interface PersonalBestStatus {
  prevDurationSeconds: number  // duration of the previous best historical streak
  prevAchievedAt: string       // when the previous best ended (ISO)
  prevStartedAt: string        // when the previous best began (ISO)
  liveStreakSeconds: number    // current live streak (0 if not sober)
  liveStartedAt: string | null // when the live streak began (ISO, null if not sober)
  isCurrent: boolean           // true when the live streak has surpassed the previous best
}

/**
 * Status of the user's personal best — both the previous record and how the
 * current live streak relates to it. Returns null when there's no historical
 * (non-live) streak to compare against.
 */
export function getPersonalBestStatus(
  soberStreaks: { startedAt: string; endedAt: string; durationSeconds: number; isLive?: boolean }[]
): PersonalBestStatus | null {
  const live = soberStreaks.find(s => s.isLive)
  const others = soberStreaks.filter(s => !s.isLive)
  if (others.length === 0) return null
  const prevBest = others.reduce((max, s) =>
    s.durationSeconds > max.durationSeconds ? s : max
  )
  const liveStreakSeconds = live?.durationSeconds || 0
  return {
    prevDurationSeconds: prevBest.durationSeconds,
    prevAchievedAt: prevBest.endedAt,
    prevStartedAt: prevBest.startedAt,
    liveStreakSeconds,
    liveStartedAt: live?.startedAt || null,
    isCurrent: liveStreakSeconds > prevBest.durationSeconds,
  }
}

/**
 * If the live streak has surpassed the longest historical (non-live) streak,
 * return a Personal Best earned milestone — earnedAt is the moment the
 * previous record was passed. Returns null when there's no prior record to beat.
 */
export function getPersonalBest(
  soberStreaks: { startedAt: string; endedAt: string; durationSeconds: number; isLive?: boolean }[]
): EarnedMilestone | null {
  const live = soberStreaks.find(s => s.isLive)
  if (!live) return null
  const others = soberStreaks.filter(s => !s.isLive)
  if (others.length === 0) return null
  const prevBest = others.reduce((max, s) =>
    s.durationSeconds > max.durationSeconds ? s : max
  )
  if (live.durationSeconds <= prevBest.durationSeconds) return null
  const earnedAt = new Date(
    new Date(live.startedAt).getTime() + prevBest.durationSeconds * 1000
  ).toISOString()
  return {
    ...PERSONAL_BEST_MILESTONE,
    durationSeconds: prevBest.durationSeconds,
    earnedAt,
    streakStartedAt: live.startedAt,
  }
}

/**
 * Format streak duration for nav display.
 */
export function formatStreakCompact(seconds: number): string {
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60)
    return `${mins}m`
  }
  if (seconds < DAY) {
    const hrs = Math.floor(seconds / 3600)
    return `${hrs}h`
  }
  const days = Math.floor(seconds / DAY)
  if (days < 365) return `${days}d`
  const years = Math.floor(days / 365)
  const remainDays = days % 365
  return remainDays > 0 ? `${years}y ${remainDays}d` : `${years}y`
}
